import { PagingParameters } from "@/api/dtos";
import {
  consumeDashboardListScroll,
  DashboardListView,
  peekDashboardListScroll,
  scrollToDashboardListItem,
} from "@/lib/dashboardListScrollRestore";
import { Dispatch, SetStateAction, useEffect, useRef } from "react";

type UseDashboardListScrollRestoreParams = {
  listKey: string;
  entityIds: string[];
  isLoading: boolean;
  pagination: PagingParameters;
  setPagination: Dispatch<SetStateAction<PagingParameters>>;
  onRestoreView?: (view: DashboardListView) => void;
};

export function useDashboardListScrollRestore({
  listKey,
  entityIds,
  isLoading,
  pagination,
  setPagination,
  onRestoreView,
}: UseDashboardListScrollRestoreParams): void {
  const restoreStartedRef = useRef(false);
  const pageAdjustedRef = useRef(false);
  const viewRestoredRef = useRef(false);

  useEffect(() => {
    if (!listKey || isLoading || restoreStartedRef.current) return;

    const pending = peekDashboardListScroll(listKey);
    if (!pending) return;

    restoreStartedRef.current = true;
  }, [isLoading, listKey]);

  useEffect(() => {
    if (!listKey || !restoreStartedRef.current || isLoading) return;

    const pending = peekDashboardListScroll(listKey);
    if (!pending) {
      restoreStartedRef.current = false;
      pageAdjustedRef.current = false;
      viewRestoredRef.current = false;
      return;
    }

    if (!viewRestoredRef.current && onRestoreView) {
      onRestoreView(pending.view);
      viewRestoredRef.current = true;
    }

    const entityLoaded = entityIds.includes(pending.entityId);

    if (!entityLoaded) {
      const needsPageAdjust =
        pending.page !== pagination.page ||
        pending.pageSize !== pagination.pageSize;

      if (needsPageAdjust && !pageAdjustedRef.current) {
        pageAdjustedRef.current = true;
        setPagination({ page: pending.page, pageSize: pending.pageSize });
        return;
      }
    }

    if (!entityLoaded) {
      const consumed = consumeDashboardListScroll(listKey);
      if (consumed) {
        requestAnimationFrame(() => {
          scrollToDashboardListItem(consumed.entityId, consumed.scrollY);
        });
      }
      restoreStartedRef.current = false;
      pageAdjustedRef.current = false;
      viewRestoredRef.current = false;
      return;
    }

    const consumed = consumeDashboardListScroll(listKey);
    if (!consumed) {
      restoreStartedRef.current = false;
      pageAdjustedRef.current = false;
      viewRestoredRef.current = false;
      return;
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        scrollToDashboardListItem(consumed.entityId, consumed.scrollY);
      });
    });

    restoreStartedRef.current = false;
    pageAdjustedRef.current = false;
    viewRestoredRef.current = false;
  }, [
    listKey,
    entityIds,
    isLoading,
    pagination.page,
    pagination.pageSize,
    setPagination,
    onRestoreView,
  ]);
}
